/**
 * Export Historical OI Data service (admin-only) — talks to
 * `/api/v1/oi-export/*` (see `app.api.v1.oi_export`). The entire router
 * requires ADMIN role server-side; every method here will 403 for a
 * non-admin caller regardless of what the UI shows. Independent of
 * `oiBackfillService` — see that module's/`app.services.oi_export.service`'s
 * own docstrings for why these two features never share backend code.
 *
 * Note on error shapes: unlike `oiBackfillService` (which reuses 422 even
 * for "job not found" to match `oi_backfill`'s own established behavior),
 * this feature uses 404 for "job not found"/"file not found" and 422 for
 * everything else (bad filters, downloading a not-yet-completed job) — see
 * `app.api.v1.oi_export`'s own ERROR SEMANTICS docstring.
 * `extractErrorMessage` already surfaces the `message` field directly
 * either way, so no special-casing is needed here.
 */

import api from "@/services/api";
import type {
  OIExportEstimateRequest,
  OIExportEstimateResponse,
  OIExportJobCreateRequest,
  OIExportJobRead,
  OIExportJobStatus,
  OIExportResolutionSummaryRequest,
  OIExportResolutionSummaryResponse,
} from "@/types";

export interface ListOIExportJobsParams {
  status?: OIExportJobStatus;
  limit?: number;
}

const EXPORT_FORMAT_EXTENSIONS: Record<string, string> = { CSV: "csv", XLSX: "xlsx", ZIP: "zip" };

export const oiExportService = {
  async estimate(body: OIExportEstimateRequest): Promise<OIExportEstimateResponse> {
    const { data } = await api.post<OIExportEstimateResponse>("/oi-export/estimate", body);
    return data;
  },

  /**
   * Pre-export data-quality summary — the actual stored resolution
   * (1-minute vs 5-minute vs other/unknown) for the given filters, derived
   * from `captured_at` gaps server-side (see
   * `app.services.oi_export.resolution`). Used to show the mixed-resolution
   * warning before a "1 Minute" export runs (requirement #6/#7).
   */
  async summarizeResolution(
    body: OIExportResolutionSummaryRequest,
  ): Promise<OIExportResolutionSummaryResponse> {
    const { data } = await api.post<OIExportResolutionSummaryResponse>(
      "/oi-export/resolution-summary",
      body,
    );
    return data;
  },

  async createJob(body: OIExportJobCreateRequest): Promise<OIExportJobRead> {
    const { data } = await api.post<OIExportJobRead>("/oi-export", body);
    return data;
  },

  async listJobs(params: ListOIExportJobsParams = {}): Promise<OIExportJobRead[]> {
    const { data } = await api.get<OIExportJobRead[]>("/oi-export", { params });
    return data;
  },

  async getJob(jobId: string): Promise<OIExportJobRead> {
    const { data } = await api.get<OIExportJobRead>(`/oi-export/${jobId}`);
    return data;
  },

  async deleteJob(jobId: string): Promise<void> {
    await api.delete(`/oi-export/${jobId}`);
  },

  /**
   * Downloads a completed export's file and triggers a client-side save —
   * this is a real, authenticated API call (the route requires the same
   * Bearer token as everything else), so a plain `<a href>` to the backend
   * URL would 401; instead this fetches the file as a `Blob` via the
   * shared `api` instance (which attaches the token) and drives the browser
   * download the same way `utils/csv.ts`'s client-generated exports do.
   */
  async downloadFile(job: OIExportJobRead): Promise<void> {
    const response = await api.get<Blob>(`/oi-export/${job.id}/download`, { responseType: "blob" });
    const extension = EXPORT_FORMAT_EXTENSIONS[job.export_format] ?? "bin";
    const filename = `${job.symbol_code}_${job.from_date}_${job.to_date}.${extension}`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};

export default oiExportService;
